import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Settings2,
  RefreshCw,
  Save,
  RotateCcw,
  ChevronRight,
  Database,
  Shield,
  Zap,
  FileText,
  Mic,
  HardDrive,
  Clock,
  Brain,
  Activity,
  AlertTriangle,
  Check,
  Info
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Section icons mapping
const SECTION_ICONS = {
  retrieval: Database,
  guardrails: Shield,
  cache: Zap,
  chunking: FileText,
  voice: Mic,
  ingestion: FileText,
  tier_manager: HardDrive,
  lifecycle: Clock,
  embedding: Brain,
  monitoring: Activity,
};

export default function RAGConfigSettings() {
  const [sections, setSections] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [sectionData, setSectionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Load sections metadata
  useEffect(() => {
    fetchSections();
  }, []);

  // Load section data when active section changes
  useEffect(() => {
    if (activeSection) {
      fetchSectionData(activeSection);
    }
  }, [activeSection]);

  const fetchSections = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/rag-config/sections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSections(response.data.sections);
      if (response.data.sections.length > 0) {
        setActiveSection(response.data.sections[0].id);
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
      toast.error("Failed to load RAG configuration");
    } finally {
      setLoading(false);
    }
  };

  const fetchSectionData = async (sectionId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/rag-config/section/${sectionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSectionData(response.data.section);
    } catch (error) {
      console.error("Error fetching section data:", error);
      toast.error("Failed to load section configuration");
    }
  };

  const handleFieldChange = (path, value) => {
    setPendingChanges(prev => ({
      ...prev,
      [path]: value
    }));
    setHasChanges(true);
  };

  const saveChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) {
      toast.info("No changes to save");
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      
      await axios.put(
        `${API}/admin/rag-config/bulk`,
        { updates: pendingChanges },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Saved ${Object.keys(pendingChanges).length} configuration changes`);
      setPendingChanges({});
      setHasChanges(false);
      
      // Refresh current section
      if (activeSection) {
        fetchSectionData(activeSection);
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!confirm("Are you sure you want to reset ALL RAG configuration to defaults? This cannot be undone.")) {
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      
      await axios.post(
        `${API}/admin/rag-config/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Configuration reset to defaults");
      setPendingChanges({});
      setHasChanges(false);
      
      // Refresh sections
      fetchSections();
    } catch (error) {
      console.error("Error resetting config:", error);
      toast.error("Failed to reset configuration");
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => {
    setPendingChanges({});
    setHasChanges(false);
    if (activeSection) {
      fetchSectionData(activeSection);
    }
    toast.info("Changes discarded");
  };

  const renderField = (field) => {
    const currentValue = pendingChanges[field.path] !== undefined 
      ? pendingChanges[field.path] 
      : field.value;
    
    const isModified = pendingChanges[field.path] !== undefined;

    switch (field.type) {
      case "boolean":
        return (
          <div key={field.path} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
            <div className="flex-1">
              <Label className="text-sm font-medium text-slate-700">{field.label}</Label>
              {field.is_override && (
                <Badge variant="secondary" className="ml-2 text-xs">Override</Badge>
              )}
              {isModified && (
                <Badge variant="outline" className="ml-2 text-xs text-amber-600 border-amber-300">Modified</Badge>
              )}
            </div>
            <Switch
              checked={currentValue}
              onCheckedChange={(checked) => handleFieldChange(field.path, checked)}
            />
          </div>
        );

      case "number":
        const isFloat = field.step < 1;
        return (
          <div key={field.path} className="py-3 border-b border-slate-100 last:border-0">
            <div className="flex items-center justify-between mb-2">
              <div>
                <Label className="text-sm font-medium text-slate-700">{field.label}</Label>
                {field.is_override && (
                  <Badge variant="secondary" className="ml-2 text-xs">Override</Badge>
                )}
                {isModified && (
                  <Badge variant="outline" className="ml-2 text-xs text-amber-600 border-amber-300">Modified</Badge>
                )}
              </div>
              <span className="text-sm font-mono text-slate-600 bg-slate-100 px-2 py-1 rounded">
                {isFloat ? currentValue?.toFixed(2) : currentValue}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Slider
                value={[currentValue || field.min]}
                onValueChange={(values) => handleFieldChange(field.path, values[0])}
                min={field.min}
                max={field.max}
                step={field.step}
                className="flex-1"
              />
              <Input
                type="number"
                value={currentValue}
                onChange={(e) => {
                  const val = isFloat ? parseFloat(e.target.value) : parseInt(e.target.value);
                  if (!isNaN(val)) {
                    handleFieldChange(field.path, val);
                  }
                }}
                min={field.min}
                max={field.max}
                step={field.step}
                className="w-24 text-right"
              />
            </div>
          </div>
        );

      case "text":
        return (
          <div key={field.path} className="py-3 border-b border-slate-100 last:border-0">
            <div className="flex items-center mb-2">
              <Label className="text-sm font-medium text-slate-700">{field.label}</Label>
              {field.is_override && (
                <Badge variant="secondary" className="ml-2 text-xs">Override</Badge>
              )}
              {isModified && (
                <Badge variant="outline" className="ml-2 text-xs text-amber-600 border-amber-300">Modified</Badge>
              )}
            </div>
            <Input
              value={currentValue || ""}
              onChange={(e) => handleFieldChange(field.path, e.target.value)}
              className="font-mono text-sm"
            />
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="rag-config-settings">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <Settings2 className="w-7 h-7 text-indigo-600" />
              RAG V4 Configuration
            </h1>
            <p className="text-slate-500 mt-1">
              Configure retrieval pipeline, guardrails, caching, and all RAG system parameters
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {hasChanges && (
              <Badge variant="outline" className="text-amber-600 border-amber-300 px-3 py-1">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {Object.keys(pendingChanges).length} unsaved changes
              </Badge>
            )}
            
            <Button
              variant="outline"
              onClick={discardChanges}
              disabled={!hasChanges || saving}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Discard
            </Button>
            
            <Button
              onClick={saveChanges}
              disabled={!hasChanges || saving}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      {/* Info Alert */}
      <Alert className="mb-6 border-blue-200 bg-blue-50">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-800">No Hardcoded Values</AlertTitle>
        <AlertDescription className="text-blue-700">
          All RAG system parameters are configurable here. Changes take effect immediately and persist across restarts.
          Use "Reset to Defaults" to revert all settings to their original values.
        </AlertDescription>
      </Alert>

      {/* Main Content */}
      <div className="grid grid-cols-12 gap-6">
        {/* Section Navigation */}
        <div className="col-span-3">
          <Card className="sticky top-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                Configuration Sections
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <nav className="space-y-1">
                {sections.map((section) => {
                  const Icon = SECTION_ICONS[section.id] || Settings2;
                  const isActive = activeSection === section.id;
                  
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                        isActive
                          ? "bg-indigo-50 text-indigo-700 border-l-2 border-indigo-600"
                          : "text-slate-600 hover:bg-slate-50 border-l-2 border-transparent"
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${isActive ? "text-indigo-600" : "text-slate-400"}`} />
                      <span className="font-medium">{section.name}</span>
                      <ChevronRight className={`w-4 h-4 ml-auto ${isActive ? "text-indigo-600" : "text-slate-300"}`} />
                    </button>
                  );
                })}
              </nav>
              
              {/* Reset Button */}
              <div className="p-4 border-t border-slate-200">
                <Button
                  variant="outline"
                  className="w-full text-red-600 border-red-200 hover:bg-red-50"
                  onClick={resetToDefaults}
                  disabled={saving}
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset All to Defaults
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Section Content */}
        <div className="col-span-9">
          {sectionData ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  {(() => {
                    const Icon = SECTION_ICONS[sectionData.id] || Settings2;
                    return <Icon className="w-6 h-6 text-indigo-600" />;
                  })()}
                  {sectionData.name}
                </CardTitle>
                <CardDescription>{sectionData.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" defaultValue={sectionData.subsections?.map(s => s.id) || []} className="space-y-4">
                  {sectionData.subsections?.map((subsection) => (
                    <AccordionItem
                      key={subsection.id}
                      value={subsection.id}
                      className="border border-slate-200 rounded-lg px-4"
                    >
                      <AccordionTrigger className="hover:no-underline">
                        <span className="font-semibold text-slate-800">{subsection.name}</span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pt-2">
                          {subsection.fields?.map(renderField)}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          ) : (
            <Card className="flex items-center justify-center py-16">
              <div className="text-center">
                <Settings2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Select a section to configure</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
